import { useMemo, useState, useCallback, memo } from "react";
import { Link } from "wouter";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Loader2, Award, Target, BarChart2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

const AUDIENCE_FILTERS = [
  { value: "all", label: "Everyone" },
  { value: "seller", label: "Sellers" },
  { value: "buyer", label: "Buyers" },
  { value: "business", label: "Business" },
] as const;

type AudienceFilter = typeof AUDIENCE_FILTERS[number]["value"];

type SurveyCampaign = {
  _id: string;
  title: string;
  description?: string;
  audience?: string[];
  rewardValue?: number;
  questions: Array<{
    id: string;
    prompt: string;
    type?: string;
    choices?: string[];
  }>;
};

type SurveyInsights = {
  metrics: {
    totalActive: number;
    totalScheduled: number;
    totalCompleted: number;
    responsesLastWindow: number;
    responsesByAudience: Record<string, number>;
  };
  suggestedDecision: null | {
    focus: string;
    audience: string[];
    reason: string;
    rewardPoints: number;
    launchDelayMinutes: number;
  };
};

// Memoized campaign card component
const CampaignCard = memo(function CampaignCard({
  campaign,
  onSelect,
}: {
  campaign: SurveyCampaign;
  onSelect: (campaign: SurveyCampaign) => void;
}) {
  const handleStart = useCallback(() => {
    onSelect(campaign);
  }, [campaign, onSelect]);

  return (
    <Card className="flex flex-col border-border/70 bg-background/80">
      <CardHeader className="space-y-3">
        <CardTitle className="text-lg font-semibold text-foreground">
          {campaign.title}
        </CardTitle>
        {campaign.description ? (
          <CardDescription className="text-sm text-muted-foreground">
            {campaign.description}
          </CardDescription>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="secondary" className="inline-flex items-center gap-1">
            <Award className="h-3 w-3" />
            +{campaign.rewardValue ?? 0} pts
          </Badge>
          {(campaign.audience ?? ["global"]).map((audience) => (
            <Badge key={audience} variant="outline">{audience}</Badge>
          ))}
        </div>
      </CardHeader>
      <CardFooter className="mt-auto flex items-center justify-between border-t border-border/60 bg-muted/40">
        <span className="text-xs text-muted-foreground">{campaign.questions.length} questions</span>
        <Button size="sm" onClick={handleStart}>
          Start mission
        </Button>
      </CardFooter>
    </Card>
  );
});

// Memoized insights metric component
const InsightsMetric = memo(function InsightsMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-background/80 p-4 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-foreground">{value}</p>
    </div>
  );
});

// Memoized survey question field
const SurveyQuestionField = memo(function SurveyQuestionField({ question, index, value, onChange }: SurveyQuestionFieldProps) {
  const questionNumber = index + 1;
  const type = question.type ?? (question.choices && question.choices.length > 0 ? "single_select" : "open_text");

  const handleChange = useCallback((next: string) => {
    onChange(question.id, next);
  }, [question.id, onChange]);

  const handleTextChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleChange(event.target.value);
  }, [handleChange]);

  return (
    <div className="space-y-3 rounded-2xl border border-border/60 bg-muted/20 p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 text-sm font-semibold text-primary">{questionNumber.toString().padStart(2, "0")}</span>
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">{question.prompt}</p>
          {type === "open_text" ? (
            <p className="text-xs text-muted-foreground">Short answer</p>
          ) : null}
        </div>
      </div>

      {type === "open_text" ? (
        <Textarea
          value={value}
          onChange={handleTextChange}
          placeholder="Share your thoughts"
          rows={4}
          className="resize-none"
        />
      ) : null}

      {type === "scale" && question.choices ? (
        <RadioGroup value={value} onValueChange={handleChange} className="grid grid-cols-5 gap-3">
          {question.choices.map((choice) => {
            const id = `${question.id}-${choice}`;
            return (
              <div key={choice} className="flex flex-col items-center gap-2">
                <RadioGroupItem value={choice} id={id} />
                <Label
                  htmlFor={id}
                  className={cn(
                    "flex h-10 w-full cursor-pointer items-center justify-center rounded-full border border-border bg-background text-xs font-medium transition hover:border-primary",
                    value === choice ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground"
                  )}
                >
                  {choice}
                </Label>
              </div>
            );
          })}
        </RadioGroup>
      ) : null}

      {type === "single_select" && question.choices ? (
        <RadioGroup value={value} onValueChange={handleChange} className="grid gap-3">
          {question.choices.map((choice) => {
            const id = `${question.id}-${choice}`;
            return (
              <div key={choice} className="flex items-center gap-3 rounded-xl border border-border bg-background/80 px-4 py-3 transition hover:border-primary">
                <RadioGroupItem value={choice} id={id} />
                <Label
                  htmlFor={id}
                  className={cn(
                    "flex w-full cursor-pointer items-center justify-between text-sm",
                    value === choice ? "text-primary" : "text-muted-foreground"
                  )}
                >
                  <span>{choice}</span>
                  {value === choice ? <Badge variant="secondary">Selected</Badge> : null}
                </Label>
              </div>
            );
          })}
        </RadioGroup>
      ) : null}

      {type === "short_text" && !question.choices ? (
        <Input
          value={value}
          onChange={handleTextChange}
          placeholder="Type your answer"
        />
      ) : null}
    </div>
  );
});

type SurveyQuestionFieldProps = {
  question: SurveyCampaign["questions"][number];
  index: number;
  value: string;
  onChange: (questionId: string, value: string) => void;
};

export default function SurveyMissions() {
  const convexApi = api as any;
  const { t } = useTranslation();
  const [audienceFilter, setAudienceFilter] = useState<AudienceFilter>("all");
  const [selectedCampaign, setSelectedCampaign] = useState<SurveyCampaign | null>(null);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const surveyArgs = useMemo(() => ({
    audience: audienceFilter === "all" ? undefined : audienceFilter,
  }), [audienceFilter]);

  const campaigns = useQuery(convexApi.surveys.listActiveCampaigns, surveyArgs) as
    | SurveyCampaign[]
    | undefined;
  const insights = useQuery(convexApi.surveys.getSurveyInsights, { windowDays: 7 }) as
    | SurveyInsights
    | undefined;

  const submitSurvey = useMutation(convexApi.surveys.submitSurveyResponse);

  // Stable callback for campaign selection
  const handleSelectCampaign = useCallback((campaign: SurveyCampaign) => {
    setSelectedCampaign(campaign);
    setResponses({});
  }, []);

  // Stable callback for clearing selection
  const handleClearCampaign = useCallback(() => {
    setSelectedCampaign(null);
    setResponses({});
  }, []);

  // Stable callback for answer changes
  const handleAnswerChange = useCallback((questionId: string, value: string) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  // Stable callback for form submission
  const handleSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedCampaign) return;

    const missing = selectedCampaign.questions.filter((question) => {
      const answer = responses[question.id];
      return !answer || answer.trim().length === 0;
    });

    if (missing.length > 0) {
      toast.error("Please answer every question before submitting.");
      return;
    }

    setIsSubmitting(true);
    try {
      await submitSurvey({
        campaignId: selectedCampaign._id,
        answers: selectedCampaign.questions.map((question) => ({
          questionId: question.id,
          value: responses[question.id],
        })),
      });

      toast.success("Thanks! Your responses were recorded and your points will arrive shortly.");
      setSelectedCampaign(null);
      setResponses({});
    } catch (error: any) {
      const message = error?.data?.message || error?.message || "Unable to submit survey right now.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedCampaign, responses, submitSurvey]);

  const filteredCampaigns = useMemo(() => campaigns ?? [], [campaigns]);

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border sticky top-0 z-40 bg-background/95 backdrop-blur">
        <div className="container flex items-center justify-between h-16">
          <Link href="/">
            <a className="text-2xl font-bold gradient-text">Listo</a>
          </Link>
          <div className="flex items-center gap-4">
            <Link href="/dashboard">
              <a className="text-foreground hover:text-primary transition-colors">{t("nav.dashboard")}</a>
            </Link>
            <Link href="/surveys">
              <a className="text-primary font-medium">{t("nav.surveys")}</a>
            </Link>
            <Link href="/profile">
              <a className="text-foreground hover:text-primary transition-colors">{t("nav.profile")}</a>
            </Link>
          </div>
        </div>
      </nav>

      <main className="container py-10 space-y-10">
        <section>
          <Card className="border-primary/30 bg-primary/5">
            <CardHeader className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <BarChart2 className="h-4 w-4 text-primary" />
                <span>Voice of customer insights (rolling 7 days)</span>
              </div>
              <CardTitle className="text-xl">Survey intelligence</CardTitle>
              <CardDescription>
                Track feedback coverage and discover where we should learn next. Automations launch research when coverage drops.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-4">
              <InsightsMetric label="Active" value={insights?.metrics.totalActive ?? 0} />
              <InsightsMetric label="Scheduled" value={insights?.metrics.totalScheduled ?? 0} />
              <InsightsMetric label="Completed" value={insights?.metrics.totalCompleted ?? 0} />
              <InsightsMetric label="Responses (7d)" value={insights?.metrics.responsesLastWindow ?? 0} />
            </CardContent>
            {insights?.suggestedDecision ? (
              <CardFooter className="flex flex-col items-start gap-3 border-t border-border/60 bg-background/60">
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <Target className="h-4 w-4 text-primary" />
                  Upcoming focus: {insights.suggestedDecision.focus}
                </div>
                <p className="text-sm text-muted-foreground">
                  {insights.suggestedDecision.reason}
                </p>
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline">Reward {insights.suggestedDecision.rewardPoints} pts</Badge>
                  <Badge variant="outline">Launch in ~{insights.suggestedDecision.launchDelayMinutes} min</Badge>
                  {insights.suggestedDecision.audience.map((audience) => (
                    <Badge key={audience} variant="secondary">{audience}</Badge>
                  ))}
                </div>
              </CardFooter>
            ) : null}
          </Card>
        </section>

        <section className="space-y-6">
          <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Live survey missions</h1>
              <p className="text-sm text-muted-foreground">
                Earn reward points for guiding the roadmap. Each mission takes about two minutes.
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background p-1">
              {AUDIENCE_FILTERS.map((filter) => (
                <Button
                  key={filter.value}
                  size="sm"
                  variant={audienceFilter === filter.value ? "default" : "ghost"}
                  className={cn("rounded-full px-4", audienceFilter === filter.value ? "bg-primary text-primary-foreground" : "text-muted-foreground")}
                  onClick={() => setAudienceFilter(filter.value)}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </header>

          {campaigns === undefined ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading missions…
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="text-base">No missions yet</CardTitle>
                <CardDescription>
                  We are not running surveys for this audience right now. Check back soon or switch filters.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filteredCampaigns.map((campaign) => (
                <CampaignCard
                  key={campaign._id}
                  campaign={campaign}
                  onSelect={handleSelectCampaign}
                />
              ))}
            </div>
          )}
        </section>

        {selectedCampaign ? (
          <section className="space-y-6 rounded-3xl border border-border bg-background/90 p-6 shadow-lg">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline">Survey mission</Badge>
                  {(selectedCampaign.audience ?? ["global"]).map((audience) => (
                    <Badge key={audience} variant="secondary">{audience}</Badge>
                  ))}
                </div>
                <h2 className="text-xl font-semibold text-foreground">{selectedCampaign.title}</h2>
                {selectedCampaign.description ? (
                  <p className="text-sm text-muted-foreground max-w-2xl">
                    {selectedCampaign.description}
                  </p>
                ) : null}
              </div>
              <Button variant="ghost" size="sm" onClick={handleClearCampaign}>
                Cancel
              </Button>
            </div>

            <Separator />

            <form className="space-y-6" onSubmit={handleSubmit}>
              {selectedCampaign.questions.map((question, index) => (
                <SurveyQuestionField
                  key={question.id}
                  question={question}
                  index={index}
                  value={responses[question.id] ?? ""}
                  onChange={handleAnswerChange}
                />
              ))}

              <div className="flex flex-wrap items-center gap-3">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting…
                    </>
                  ) : (
                    "Submit survey"
                  )}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Complete this mission to earn {selectedCampaign.rewardValue ?? 0} points.
                </p>
              </div>
            </form>
          </section>
        ) : null}
      </main>
    </div>
  );
}
